import { QueryClient, useQuery } from "@tanstack/react-query";

import { API, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

import {
  getEvent,
  listEventAttendees,
  getCategorizedEvent,
  eventAttendeesByEventAttendeeUserId,
  eventAttendeesByEventAttendeeId,
  eventsByOrganizationGroupAndStartTime,
  getListContext,
} from "@/graphql/queries";

import { customGetListContext, customListEvents } from "@/graphql/customQueries/index";

import {
  joinEvent,
  deleteEvent,
  deleteEventAttendee,
  inviteUserToEvent,
  createEvent,
  updateEvent,
  updateEventAttendee,
  assignNewAdminToContextBulk,
  recordVisitByContext,
  addNewEvent,
  deleteCircleEventPost,
} from "@/graphql/mutations";

import {
  IdataCreateEvent,
  IUpdateInvitedEvent,
  IDataUpdateEvent,
  IUpdateEventMutation,
  ILeaveEventMutation,
  IJoinEventMutation,
  IDataDeleteEvent,
  IUpdateInvitedEventMutation,
  InviteEventMutation,
  IDeleteEventMutation,
  IListEventsQueryVariables,
} from "@/types";
import { sortRecordsByKey } from "@/utils/sortGraphqlRecords";
import { eventsByOrganizationGroupAndStartTimeCustom } from "@/graphql/customQueries/listEvents";
import useCircleEventStore, { IcircleEventStore } from "./useCircleEventStore";
import dayjs from "dayjs";
import { Event } from "@/models";
import { fetchCGPercentContext } from "./useCircles";
import useVisitOtherProfileStore, { IVisitOtherProfileStore } from "./useVisitOtherProfileStore";
import { getEventCustom } from "@/graphql/customQueries/getEventCustom";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const fetchListEvents = async (
  sortBy: string,
  ascending: boolean,
  limit: number = 1000,
) => {
  const variables: IListEventsQueryVariables = {
    filter: { _deleted: { ne: true } },
    limit: limit,
  };

  const response: any = await API.graphql(
    graphqlOperation(customListEvents, variables),
  );
  const records: any = response?.data?.listEvents ?? {};

  const sortedData = sortRecordsByKey(records.items, sortBy, ascending);

  return { ...records, items: sortedData };
};

const fetchMyInvitedEvents = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(eventAttendeesByEventAttendeeUserId, {
      eventAttendeeUserId: id,
      filter: {
        // eventAttendeeUserId: { eq: id },
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    }),
  );
  const records: any =
    response?.data?.eventAttendeesByEventAttendeeUserId?.items || {};

  return records;
};

const fetchEventAttendees = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listEventAttendees, {
      filter: {
        eventAttendeeId: { eq: id },
      },
    }),
  );
  const records: any[] = response?.data?.listEventAttendees?.items || [];

  return records;
};

const fetchEventByAttendee = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listEventAttendees, {
      filter: {
        eventAttendeeUserId: { eq: id },
      },
    }),
  );
  const records: any[] = response?.data?.listEventAttendees?.items || [];

  return records;
};

const fetchEvent = async (id: string, userId?: string) => {
  const response: any = await API.graphql(
    graphqlOperation(getEventCustom, { id: id }),
  );
  const records: any = response?.data?.getEvent ?? {};
  if(records?.isHidden) {
    if(records?.admins?.includes(userId) || records?.owner === userId || records?.attendee?.items?.some((item: any) => item.eventAttendeeUserId === userId && item.status === "APPROVED")) {
      return records;
    } else {
      return {
        ...records,
        notFound: true, // Indicate that the event is not found for this user
      }
    }
  }
  return records;
};

const fetchEventUpcoming = async (userId: string) => {
  const response: any = await API.graphql(
    graphqlOperation(getCategorizedEvent, { userId }),
  );
  const records: any = response?.data?.getCategorizedEvent ?? {};

  return records;
};

const createMyEvent = async (payload: IdataCreateEvent) => {
  const records = await API.graphql<GraphQLQuery<any>>({
    query: addNewEvent,
    variables: payload
  });

  return records?.data?.addNewEvent;
};

const inviteUser = async (
  eventId: string,
  ids: string[],
  note?: string,
  isHR?: boolean,
) => {
  const records = await API.graphql<GraphQLQuery<InviteEventMutation>>({
    query: inviteUserToEvent,
    variables: { eventId, targetUserIds: ids, note, isHR },
  });

  return records?.data?.inviteUserToEvent;
};

const updateInvitedEvent = async (payload: IUpdateInvitedEvent) => {
  return await API.graphql<GraphQLQuery<IUpdateInvitedEventMutation>>({
    query: updateEventAttendee,
    variables: { input: payload },
  });
};

const userJoinEvent = async (id: string) => {
  return await API.graphql<GraphQLQuery<IJoinEventMutation>>({
    query: joinEvent,
    variables: { eventId: id },
  });
};

const userLeaveEvent = async (userId: string, version: string) => {
  return await API.graphql<GraphQLQuery<ILeaveEventMutation>>({
    query: deleteEventAttendee,
    variables: {
      input: {
        id: userId,
        _version: version,
      },
    },
  });
};

const deleteMyEvent = async (id: string, version: string) => {
  const payload: IDataDeleteEvent = {
    id,
    _version: version,
  };

  return await API.graphql<GraphQLQuery<IDeleteEventMutation>>({
    query: deleteEvent,
    variables: { input: payload },
  });
};

const updateMyEvent = async (payload: IDataUpdateEvent) => {
  return await API.graphql<GraphQLQuery<IUpdateEventMutation>>({
    query: updateEvent,
    variables: { input: payload },
  });
};

const useListEventsClosestDate = (
  orgGroup: string,
  limit: number = 1000,
  enabled?: boolean,
  id?: string,
) => {
  return useQuery({
    queryKey: ["KEY_LIST_EVENTS_CLOSEST_DATE", orgGroup],
    queryFn: async () => {
      const variables: any = {
        organizationGroup: orgGroup,
        limit: limit,
      };

      const response: any = await API.graphql(
        graphqlOperation(
          eventsByOrganizationGroupAndStartTimeCustom,
          variables,
        ),
      );
      const records: any =
        response?.data?.eventsByOrganizationGroupAndStartTime ?? {};

      // const sortedData = sortRecordsByKey(records.items, sortBy, ascending);

      return { ...records };
    },
    enabled: enabled || true,
  });
};

const useListEvents = (
  sortBy: string,
  ascending: boolean = true,
  limit: number = 1000,
) => {
  return useQuery({
    queryKey: ["KEY_LIST_EVENTS", limit],
    queryFn: () => fetchListEvents(sortBy, ascending, limit),
  });
};

const useGetEventInvited = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_EVENT_INVITED", id],
    queryFn: () => fetchMyInvitedEvents(id),
  });
};

const useGetEventAttendees = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_EVENT_INVITED", id],
    queryFn: () => fetchEventAttendees(id),
  });
};

const useGetEventByAttendee = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_EVENT_BY_INVITED", id],
    queryFn: () => fetchEventByAttendee(id),
  });
};

const useGetEvent = (id: string, userId?: string) => {
  return useQuery({
    queryKey: ["KEY_GET_EVENT", id],
    queryFn: () => fetchEvent(id, userId),
  });
};

const useGetEventUpcoming = (userId: string) => {
  return useQuery({
    queryKey: ["KEY_GET_EVENT_UPCOMING", userId],
    queryFn: () => fetchEventUpcoming(userId),
  });
};

const prefetchListEvents = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["PREFETCH_LIST_EVENTS"],
    queryFn: () => fetchListEvents("startTime", false),
  });
};

const useGetEventInviteeUser = (eventId: string) => {
  return useQuery({
    queryKey: ["KEY_GET_USER_CIRCLE_INVITE", eventId],
    queryFn: async () => {
      const response: any = await API.graphql({
        query: eventAttendeesByEventAttendeeId,
        variables: {
          eventAttendeeId: eventId,
          filter: {
            status: {
              eq: "APPROVED",
            },
            _deleted: { ne: true },
          },
        },
      });

      return response?.data?.eventAttendeesByEventAttendeeId?.items;
    },
  });
};

const assignNewAdminInContext = async (
  payload: {
    contextId: string;
    contextType: "CIRCLE" | "EVENT";
    isManagedByHR: boolean;
    userId?: string | null;
  }[],
) => {
  return await API.graphql<GraphQLQuery<any>>({
    query: assignNewAdminToContextBulk,
    variables: {
      data: payload,
    },
  });
};

type setGlobalState = (p: IcircleEventStore) => void;

export const fetchMyEvents = async (id: string, setState: setGlobalState) => {
  const response = (await API.graphql(
    graphqlOperation(getListContext, {
      id,
      context: "EVENT",
    }),
  )) as any;

  const records = (response?.data?.getListContext?.events as Event[]) || [];

  const dataMyEventsCore = () =>
    (records?.filter((item) =>
      // @ts-ignore
      item.attendee?.items.some(
        (row: any) =>
          row.eventAttendeeUserId === id && row.status === "APPROVED",
      ),
    ) as Event[]) || ([] as Event[]);

  const dataMyEvents = () => {
    const activeEvents = dataMyEventsCore()?.filter((x: any) =>
      dayjs(x?.endTime).isAfter(dayjs()),
    );
    const archivedEvents = dataMyEventsCore()?.filter((x: any) =>
      dayjs(x?.endTime).isBefore(dayjs()),
    );

    return {
      active: activeEvents,
      archived: archivedEvents?.sort((a, b) => dayjs(b.endTime).diff(dayjs(a.endTime))),
    };
  };

  const dataAllEvents = () => {
    const nonJoined =
      (records?.filter((item: any) =>
        item.attendee?.items.some((row: any) => row.eventAttendeeUserId !== id),
      ) as Event[]) || ([] as Event[]);
    const filterActiveEventOnly = (nonJoined || [])?.filter((x) =>
      dayjs(x?.endTime).isAfter(dayjs()),
    );
    return filterActiveEventOnly;
  };

  setState({
    myEvents: dataMyEvents().active,
    archivedEvents: dataMyEvents().archived,
    allEvents: dataAllEvents(),
  });
};

export const useGetMyEvents = (id: string) => {
  const { setState } = useCircleEventStore();
  return useQuery({
    queryKey: ["GET_MY_EVENT", id],
    queryFn: () => fetchMyEvents(id, setState),
    enabled: false,
  });
};

type setGlobalStateVisit = (p: IVisitOtherProfileStore) => void;

export const fetchVisitProfileEvents = async (id: string, setState: setGlobalStateVisit) => {
  const response = (await API.graphql(
    graphqlOperation(getListContext, {
      id,
      context: "EVENT",
    }),
  )) as any;

  const records = (response?.data?.getListContext?.events as Event[]) || [];

  const dataMyEventsCore = () =>
    (records?.filter((item) =>
      // @ts-ignore
      item.attendee?.items.some(
        (row: any) =>
          row.eventAttendeeUserId === id && row.status === "APPROVED",
      ),
    ) as Event[]) || ([] as Event[]);

  const dataMyEvents = () => {
    const activeEvents = dataMyEventsCore()?.filter((x: any) =>
      dayjs(x?.endTime).isAfter(dayjs()),
    );
    const archivedEvents = dataMyEventsCore()?.filter((x: any) =>
      dayjs(x?.endTime).isBefore(dayjs()),
    );

    return {
      active: activeEvents,
      archived: archivedEvents,
    };
  };

  setState({
    events: [...dataMyEvents().active, ...dataMyEvents().archived],
  });
};

export const useGetVisitProfileEvents = (id: string) => {
  const { setState } = useVisitOtherProfileStore();
  return useQuery({
    queryKey: ["GET_VISIT_EVENT", id],
    queryFn: () => fetchVisitProfileEvents(id, setState),
    enabled: false,
  });
};

export const fetchRecommendedEvents = async (
  id: string,
  setState: setGlobalState,
) => {
  const response = (await API.graphql(
    graphqlOperation(customGetListContext, {
      id,
      context: "RECOMMEND_EVENT",
    }),
  )) as any;

  const records =
    (response?.data?.getListContext?.recommendedEvents as Event[]) || [];
  
  const dataEventsId = records?.map((item) => item?.id) || [];

  const CGPEvents = await fetchCGPercentContext({
    referenceIds: dataEventsId,
    type: "EVENT",
    limit: Number(dataEventsId?.length),
  });

  setState({ allEvents: records?.filter(x => !x?.isHidden), allEventsCGP: CGPEvents });
  return records;
};

export const useGetRecommendedEvents = (id: string) => {
  const { setState } = useCircleEventStore();
  return useQuery({
    queryKey: ["GET_RECOMMENDED_EVENTS"],
    queryFn: () => fetchRecommendedEvents(id, setState),
    enabled: false,
  });
};

export const updateEventAttendeeVisit = async (eventId: string, userId: string) => {
  await API.graphql(
    graphqlOperation(recordVisitByContext, {
      contextId: eventId,
      contextType: "EVENT"
    }),
  )
}

export const deleteEventPost = async (id: string) => {
  try {
    await API.graphql({
      query: deleteCircleEventPost,
      variables: {
        id,
        type: "EVENT",
      },
    });
    return;
  } catch (error) {
    console.error("Error deleting circle post:", error);
    return null;
  }
};


export {
  useListEvents,
  useGetEvent,
  userJoinEvent,
  deleteMyEvent,
  createMyEvent,
  updateMyEvent,
  inviteUser,
  useGetEventInvited,
  useGetEventAttendees,
  userLeaveEvent,
  updateInvitedEvent,
  fetchEvent,
  useGetEventUpcoming,
  useGetEventByAttendee,
  prefetchListEvents,
  useGetEventInviteeUser,
  useListEventsClosestDate,
  assignNewAdminInContext,
};
